/* eslint-disable jira/restricted/fedramp-isolated-cloud */

import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
// eslint-disable-next-line jira/restricted/@jira+issue__context-service
import { getViewModeFromLocalStorage } from '@jira/issue__context-service/src/getViewModeFromLocalStorage.tsx';
import { currentUserResource } from '@jira/router-resources__current-user/src/useCurrentUserResource.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';

import { aiEntitlementResource } from '@jira/router-resources__ai-entitlement/src/aiEntitlementResource.tsx';
import { getBoardDetailsResource } from '@jira/router-resources__classic-projects/src/getBoardDetailsResource.tsx';
import { getEditModelResource } from '@jira/router-resources__classic-projects/src/getEditModelResource.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
	shouldLoadUIFBoardResources,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { coloredLabelsResource } from '@jira/router-resources__colored-labels/src/coloredLabelsResource.tsx';
import { isFedRamp } from '@atlassian/atl-context';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { pageLoadIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeBoardModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';

import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { nextBestTaskFeatureResource } from '@jira/router-resources__next-best-task/src/nextBestTaskFeatureResource.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import {
	uifBoardResource,
	boardViewSettingsResource,
} from '@jira/router-resources__uif-board/src/uifBoardResource.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { boardViewAgentDataResource } from '@jira/router-resources__classic-projects/src/boardViewAgentDataResource.tsx';
import { classicSoftwareBoardRoute } from '@jira/router-routes__software-classic-board-routes/src/classicSoftwareBoardRoute.tsx';
import { UIFBoardSpa } from '@jira/router-routes__user-board-entries/src/ui/async.tsx';
import { isLocalStorageAvailable } from '@jira/platform__browser-storage-providers/src/controllers/local-storage/utils.tsx';
import { ClassicBoardSkeleton } from '@jira/platform__skeletons/src/ui/classic-board/ClassicBoardSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { projectThemeResource } from '@jira/router-resources__project-theme/src/projectThemeResource.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { classicBoardCapability } from './common/constants.tsx';
import {
	classicBoardPageEntryPoint,
	GSNClassicBoardPageEntryPoint,
} from './ui/page-entry-point/index.tsx';
import type { Route } from '@atlassian/react-resource-router';

type ViewModeType = 'MODAL' | 'SIDEBAR' | 'DEFAULT';

/**
 * Ordinarily it would be inadvisable to evaluate something like this in
 * a static route, as this could lead to inconsistent behaviour between
 * the server and the browser.
 *
 * In this case it is necessary and safe because:
 * 1) There are two versions of the board that share the same route
 *    and we can only tell them apart at runtime. When the old version
 *    is eventually removed, this should be cleaned up.
 * 2) No requests for board data are made on the server, so even
 *    if the value is incorrect on the server it won't be utilised
 * */

const getLayout = () => {
	let viewMode: ViewModeType = 'DEFAULT';
	if (isLocalStorageAvailable()) {
		viewMode = getViewModeFromLocalStorage();
	}
	return composeLayouts(
		genericProjectLayout,
		pageLoadIssuePreloadLayoutBuilder([{ query: ['selectedIssue'] }], viewMode),
	);
};

const isColoredLabelsMilestone2ExperimentEnabled = () => {
	return (
		expVal(
			'adding-color-to-labels-field-experiment-milestone2',
			'isColorfulNewLabelsEnabled',
			false,
		) !== false
	);
};

const getBoardEntryPoint = () => {
	if (fg('navx-global-side-nav')) {
		return GSNClassicBoardPageEntryPoint;
	}
	return classicBoardPageEntryPoint;
};

export const classicSoftwareBoardRouteEntry: Route = createEntry(classicSoftwareBoardRoute, () => ({
	group: 'classic-software',
	ufoName: 'classic-board',
	skeleton: ClassicBoardSkeleton,
	layout: getLayout(),
	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavJira,
		sidebarId: SidebarIdContent,
	},
	resources: [
		...getNavigationResources({ isUsingSidebarEntrypoint: true }),
		...getIssueViewResources(),
		projectContextResource,
		staticProjectSidebarNavigationResource,
		...getRapidboardResources(),
		getRapidBoardDataResource(),
		resourceWithCondition(
			() => shouldLoadUIFBoardResources() || fg('enable_ssr_for_cmp_board'),
			uifBoardResource,
		),
		resourceWithCondition(
			() => fg('board_ssr_view_setting_persistence'),
			boardViewSettingsResource,
		),
		resourceWithCondition(() => fg('board_ssr_missing_element_fix'), currentUserResource),
		// Load the NBT resource if we opt-in UIF, not fedRamp is enabled
		resourceWithCondition(
			() =>
				shouldLoadUIFBoardResources() &&
				(fg('aem_rr_classic_board')
					? isFeatureEnabled('jira', 'jira-next-best-task')
					: !isFedRamp()),
			nextBestTaskFeatureResource,
		),
		getEditModelResource(),
		getBoardDetailsResource(),
		forgeBoardModuleResource,
		projectThemeResource,
		resourceWithCondition(() => fg('ai_sprint_goal_gate'), aiEntitlementResource),
		resourceWithCondition(isColoredLabelsMilestone2ExperimentEnabled, coloredLabelsResource),
		resourceWithCondition(
			() => fg('assign-agents-inline_m2_technical'),
			boardViewAgentDataResource,
		),
	],
	afterPaint: [LazyIssueApp],
	forPaint: [LazyAtlassianNavigation, UIFBoardSpa],
	preloadOptions: {
		earlyChunks: ['software-cmp-board-early-entry'],
	},
	meta: {
		capability: classicBoardCapability,
	},
	earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,
	bifrost: {
		bifrostGroup: 'backlog-and-board',
		manifestName: 'jira-spa-software-board.json',
	},
	entryPoint: () => getBoardEntryPoint(),
	entryPointOptions: { isSSRPlaceholderEnabled: true },
	supportsPreviewPanel: true,
	supportsProjectTheme: 'full',
	isHydrateRootEnabled: fg('cmp-board-hydrate-root'),
	isStreamingEnabled: fg('jfp-magma-ssr-streaming-enable-boards'),
	excludeFromWrmDeferredLoading: true,
}));
